<script setup lang="ts">
import { __, n__ } from '@@/bits/intl'
import OzContainedButton, { OzContainedButtonSizePreset } from '@@/library/v4/components/OzContainedButton.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from '@@/library/v4/components/OzPlainButton.vue'
import { useResizeObserver } from '@@/vuecomposables/resize_observer'
import { debounce } from 'es-toolkit'
import type { Ref } from 'vue'
import { ref, toRefs } from 'vue'

const emit = defineEmits<{
  (e: 'toggle-poll-results'): void
  (e: 'vote'): void
  (e: 'resize', { width, height }: { width: number; height: number }): void
  (e: 'dblclick', event: MouseEvent): void
}>()

const props = withDefaults(
  defineProps<{
    totalVotesCount: number
    isPollClosed: boolean
    isShowingPollResults: boolean
    darkMode?: boolean | 'auto'
    isVoteButtonDisabled: boolean
    xVoteButton: boolean
    xResultsToggle: boolean
    isSlideshow?: boolean
    isExpanded?: boolean
    isStream?: boolean
    pollScaleMultiplier?: number
  }>(),
  { darkMode: 'auto', isExpanded: false, isSlideShow: false, isStream: false, pollScaleMultiplier: 1 },
)
const {
  darkMode,
  isPollClosed,
  totalVotesCount,
  isShowingPollResults,
  isVoteButtonDisabled,
  xVoteButton,
  xResultsToggle,
  isExpanded,
  isSlideshow,
  isStream,
} = toRefs(props)

const togglePollResults = () => {
  emit('toggle-poll-results')
}

const bottomSection = ref<HTMLElement>() as Ref<HTMLElement>
const contentTooWide = ref(false)
useResizeObserver(
  bottomSection,
  debounce(() => {
    if (bottomSection.value) {
      const width = bottomSection.value.clientWidth
      const height = bottomSection.value.clientHeight

      // 160 and 143 are max widths of the bottom section that still allow all content to be in the same row
      contentTooWide.value = (width < 160 && !isPollClosed.value) || (width < 143 && isPollClosed.value)
      emit('resize', {
        width,
        height,
      })
    }
  }, 50),
)
</script>
<template>
  <div
    ref="bottomSection"
    :class="[
      'w-full box-border',
      !isStream && !isSlideshow && !isExpanded && 'ps-3 pe-2 py-2',
      isStream && !isSlideshow ? 'px-4 py-2' : isExpanded && 'p-2',
      isSlideshow && [
        {
          'ps-9 pe-5 py-5': pollScaleMultiplier === 2.5,
          'ps-7 pe-4 py-4': pollScaleMultiplier === 2,
          'ps-6 pe-4 py-2': pollScaleMultiplier === 1,
          'bg-white-100': darkMode === false,
          'bg-nightfall-900': darkMode === true,
          'bg-white-100 dark:bg-nightfall-900': darkMode === 'auto',
        },
      ],
      isExpanded || isSlideshow ? 'min-h-12' : 'min-h-10',
      isExpanded || isSlideshow ? 'items-start' : 'items-center',
      contentTooWide ? 'flex flex-col space-y-3' : 'flex flex-wrap justify-between',
    ]"
    @dblclick.self="$emit('dblclick', $event)"
  >
    <div
      :class="[
        isStream ? 'text-body-small' : 'text-body-extra-small',
        contentTooWide ? 'flex flex-col items-start w-full space-y-3' : 'flex flex-wrap items-center space-x-1',
        {
          'text-dark-text-200': darkMode === false,
          'text-light-text-200': darkMode === true,
          'text-dark-text-200 dark:text-light-text-200': darkMode === 'auto',
        },
      ]"
    >
      <span data-testid="surfacePostPollVoteCount">
        {{
          n__('%{totalVotesCount} vote', '%{totalVotesCount} votes', totalVotesCount, {
            totalVotesCount: totalVotesCount,
          })
        }}
      </span>
      <template v-if="xResultsToggle">
        <span
          v-if="!contentTooWide"
          :class="[
            'font-semibold',
            {
              'text-dark-text-300': darkMode === false,
              'text-light-text-300': darkMode === true,
              'text-dark-text-300 dark:text-light-text-300': darkMode === 'auto',
            },
          ]"
          >•</span
        >
        <OzPlainButton
          :class="[
            'font-semibold rounded-xl focus-visible:ring-2 px-0',
            {
              'text-dark-text-200': darkMode === false,
              'text-light-text-200': darkMode === true,
              'text-dark-text-200 dark:text-light-text-200': darkMode === 'auto',
            },
          ]"
          :tabindex="0"
          :dark-mode="darkMode"
          :text="isShowingPollResults ? __('Hide Results') : __('Show Results')"
          :color-scheme="OzPlainButtonColorScheme.Secondary"
          :size-preset="OzPlainButtonSizePreset.Bare"
          :data-testid="isShowingPollResults ? 'surfacePostPollHideResultsButton' : 'surfacePostPollShowResultsButton'"
          @click="togglePollResults"
          @pointerdown.stop
        />
      </template>
      <template v-else-if="isPollClosed">
        <span
          v-if="!contentTooWide"
          :class="{
            'text-dark-text-300': darkMode === false,
            'text-light-text-300': darkMode === true,
            'text-dark-text-300 dark:text-light-text-300': darkMode === 'auto',
          }"
          >•</span
        >
        <span
          data-testid="surfacePostPollClosedText"
          :class="{
            'text-dark-text-200': darkMode === false,
            'text-light-text-200': darkMode === true,
            'text-dark-text-200 dark:text-light-text-200': darkMode === 'auto',
          }"
        >
          {{ __('Poll closed') }}
        </span>
      </template>
    </div>

    <OzContainedButton
      v-if="xVoteButton"
      :class="['no-drag', contentTooWide && 'w-full']"
      data-testid="surfacePostPollVoteButton"
      :size-preset="
        isSlideshow || isExpanded || isStream ? OzContainedButtonSizePreset.H32px : OzContainedButtonSizePreset.H24px
      "
      :dark-mode="darkMode"
      :text="__('Vote')"
      :label="__('Vote')"
      :disabled="isVoteButtonDisabled"
      @click.stop="emit('vote')"
      @pointerdown.stop
    />
  </div>
</template>
